<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { required, maxLength } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import SizeDO = Modules.CampaignPlanner.Data.SizeDO;
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

const { t } = useI18n();

const props = defineProps<{
	sizeToEdit: SizeDO | null;
}>();

const emit = defineEmits<{
	"update-list": [];
}>();

const isDialogVisible = defineModel<boolean>();

const isNew = computed(
	() =>
		props.sizeToEdit === null ||
		props.sizeToEdit.id === undefined ||
		props.sizeToEdit.id === null
);

type LocalSize = {
	label: string;
	orientation: string;
	inventory_type: string;
	location: string;
	screen_ids: number[];
};

const localSize = ref<LocalSize>({
	label: "",
	orientation: "Landscape",
	inventory_type: "Digital",
	location: "Road",
	screen_ids: [],
});

const allScreens = ref<ScreenDO[]>([]);
const formRef = ref();

const availableScreens = computed(() =>
	allScreens.value.filter(
		(s) =>
			s.inventory_type?.toLowerCase() ===
			localSize.value.inventory_type.toLowerCase()
	)
);

const screenById = computed(
	() => new Map(allScreens.value.map((s) => [s.id, s]))
);

const rawToId = (raw: unknown): number =>
	typeof raw === "object" && raw !== null ? (raw as ScreenDO).id : (raw as number);

const matchedScreenIdSet = computed(
	() => new Set(localSize.value.screen_ids.filter((id) => screenById.value.has(id)))
);

const selectionLabel = (raw: unknown): string => {
	const id = rawToId(raw);
	return screenById.value.get(id)?.reference_id ?? String(id);
};

watch(
	() => props.sizeToEdit,
	(size) => {
		if (size && size.id) {
			localSize.value = {
				label: size.label,
				orientation: size.orientation,
				inventory_type: size.inventory_type,
				location: size.location,
				screen_ids: size.screens?.map((s) => s.id) ?? [],
			};
		} else {
			localSize.value = {
				label: "",
				orientation: "Landscape",
				inventory_type: "Digital",
				location: "Road",
				screen_ids: [],
			};
		}
	},
	{ immediate: true }
);

onMounted(async () => {
	const response = await useApiClient("campaignPlanner").getScreens({
		page: 1,
		per_page: 1000,
		"filter[active]": "true",
	});
	if (response.isOk()) {
		allScreens.value = response.value.data;
	}
});

const save = async () => {
	const { valid } = await formRef.value.validate();
	if (!valid) return;

	const payload = { ...localSize.value };

	const response = await toastApiResult(
		isNew.value
			? useApiClient("campaignPlanner").createSize(payload)
			: useApiClient("campaignPlanner").updateSize(
					props.sizeToEdit!.id!,
					payload
			  )
	);

	if (response.isOk()) {
		isDialogVisible.value = false;
		emit("update-list");
	}
};

const screenItemTitle = (item: ScreenDO) => {
	const parts = [item.reference_id, item.name].filter(Boolean).join(" - ");
	return item.size ? `${parts} (Assigned)` : parts;
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" max-width="600" persistent>
		<v-card>
			<v-toolbar color="lightprimary" elevation="0" flat>
				<template #prepend>
					<v-icon class="ml-3"
						>fa: fa-regular fa-arrows-left-right-to-line</v-icon
					>
				</template>
				<v-toolbar-title>{{
					isNew ? "Add size" : "Edit size"
				}}</v-toolbar-title>
				<template #append>
					<v-btn icon variant="text" @click="isDialogVisible = false">
						<v-icon>fa: fa-regular fa-xmark</v-icon>
					</v-btn>
				</template>
			</v-toolbar>

			<v-divider />

			<v-form ref="formRef">
				<v-card-text class="pa-6">
					<v-row>
						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-semibold mb-1">Label</div>
							<v-text-field
								v-model="localSize.label"
								variant="filled"
								clearable
								hide-details="auto"
								:rules="[required(t), maxLength(t, 255)]"
							/>
						</v-col>

						<v-col cols="12">
							<v-divider class="my-2" />
						</v-col>

						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-semibold mb-2">
								Orientation
							</div>
							<v-btn-toggle
								v-model="localSize.orientation"
								mandatory
								density="compact"
								variant="outlined"
								color="primary"
								rounded="lg"
							>
								<v-btn value="Landscape" size="small">Landscape</v-btn>
								<v-btn value="Portrait" size="small">Portrait</v-btn>
							</v-btn-toggle>
						</v-col>

						<v-col cols="12">
							<v-divider class="my-2" />
						</v-col>

						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-semibold mb-2">
								Inventory
							</div>
							<v-btn-toggle
								v-model="localSize.inventory_type"
								mandatory
								density="compact"
								variant="outlined"
								color="primary"
								rounded="lg"
								@update:model-value="localSize.screen_ids = []"
							>
								<v-btn value="Digital" size="small">Digital</v-btn>
								<v-btn value="Static" size="small">Static</v-btn>
							</v-btn-toggle>
						</v-col>

						<v-col cols="12">
							<v-divider class="my-2" />
						</v-col>

						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-semibold mb-2">
								Location
							</div>
							<v-btn-toggle
								v-model="localSize.location"
								mandatory
								density="compact"
								variant="outlined"
								color="primary"
								rounded="lg"
							>
								<v-btn value="Road" size="small">Road</v-btn>
								<v-btn value="City" size="small">City</v-btn>
							</v-btn-toggle>
						</v-col>

						<v-col cols="12">
							<v-divider class="my-2" />
						</v-col>

						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-semibold mb-1">
								Screens
							</div>
							<div class="text-body-2 text-medium-emphasis mb-2">
								Filtered to screens matching the selected inventory type.
							</div>
							<v-autocomplete
								:key="localSize.inventory_type"
								v-model="localSize.screen_ids"
								variant="filled"
								:items="availableScreens"
								:item-title="screenItemTitle"
								item-value="id"
								multiple
								clearable
								hide-details
							>
								<template v-slot:selection="{ item, index }">
									<v-chip v-if="index < 3 && matchedScreenIdSet.has(rawToId(item.raw))" size="small">
										{{ selectionLabel(item.raw) }}
									</v-chip>
									<span
										v-if="index === 3"
										class="text-caption text-medium-emphasis align-self-center ml-1"
									>
										(+{{ localSize.screen_ids.length - 3 }} others)
									</span>
								</template>
							</v-autocomplete>
						</v-col>
					</v-row>
				</v-card-text>
			</v-form>

			<v-divider />

			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" @click="isDialogVisible = false">Cancel</v-btn>
				<v-btn variant="tonal" color="primary" @click="save">Save</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss"></style>
